/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { jsx, cssMap } from '@atlaskit/css';
import Skeleton from '@atlaskit/skeleton';
import { Box } from '@atlaskit/primitives/compiled/box';
import { Stack } from '@atlaskit/primitives/compiled/stack';
import { Inline } from '@atlaskit/primitives/compiled/inline';
import { Flex } from '@atlaskit/primitives/compiled/flex';
import { token } from '@atlaskit/tokens';
import { defineMessage, useIntl } from '@atlassian/jira-intl';
import { EmbedHeaderSkeleton } from './EmbedHeaderSkeleton.tsx';

/**
 * Skeleton for queue-settings.
 * QueueSettingsSkeleton: full page skeleton with header (for route entries).
 * QueueSettingsContentSkeleton: content only (for Placeholder fallback where header is already rendered).
 */

const loadingMessage = defineMessage({
	id: 'servicedesk-solution-composer.queue-settings-skeleton.loading',
	defaultMessage: 'Loading queue settings',
	description: 'Aria label for the queue settings skeleton',
});

const styles = cssMap({
	wrapper: {
		height: '100vh',
		overflow: 'hidden',
		flexDirection: 'column',
		gap: token('space.250'),
	},
	contentWrapper: {
		flex: '1',
		overflow: 'auto',
	},
	container: {
		paddingInline: token('space.400'),
		paddingBlockStart: token('space.300'),
	},
	tableHeaderRow: {
		paddingBlock: token('space.100'),
		borderBlockEndWidth: token('border.width'),
		borderBlockEndStyle: 'solid',
		borderBlockEndColor: token('color.border'),
		marginBlockEnd: token('space.100'),
	},
	tableRow: {
		paddingBlock: token('space.150'),
		borderBlockEndWidth: token('border.width'),
		borderBlockEndStyle: 'solid',
		borderBlockEndColor: token('color.border'),
	},
	actionBar: {
		paddingBlockEnd: token('space.300'),
	},
});

const QueueRow = ({ nameWidth }: { nameWidth: number }) => (
	<Box xcss={styles.tableRow} aria-hidden="true">
		<Inline spread="space-between" alignBlock="center">
			<Skeleton
				width={nameWidth}
				height={token('space.200')}
				borderRadius={token('radius.xsmall')}
				isShimmering
			/>
			<Inline space="space.100" alignBlock="center">
				<Skeleton
					width={72}
					height={token('space.200')}
					borderRadius={token('radius.xsmall')}
					isShimmering
				/>
				<Skeleton width={28} height={28} borderRadius={token('radius.medium')} isShimmering />
			</Inline>
		</Inline>
	</Box>
);

export const QueueSettingsContentSkeleton = (): JSX.Element => {
	const { formatMessage } = useIntl();
	const queueNameWidths = [200, 160, 220, 180, 240];

	return (
		<Box
			xcss={styles.container}
			role="status"
			aria-busy="true"
			aria-label={formatMessage(loadingMessage)}
		>
			<Stack space="space.0">
				{/* Action bar: search + create button */}
				<Inline spread="space-between" alignBlock="center" xcss={styles.actionBar}>
					<Skeleton
						width={280}
						height={token('space.400')}
						borderRadius={token('radius.medium')}
						isShimmering
					/>
					<Skeleton
						width={120}
						height={token('space.400')}
						borderRadius={token('radius.medium')}
						isShimmering
					/>
				</Inline>

				{/* Table column headers */}
				<Box xcss={styles.tableHeaderRow}>
					<Inline spread="space-between" alignBlock="center">
						<Skeleton
							width={60}
							height={token('space.150')}
							borderRadius={token('radius.xsmall')}
							isShimmering
						/>
						<Inline space="space.100">
							<Skeleton
								width={90}
								height={token('space.150')}
								borderRadius={token('radius.xsmall')}
								isShimmering
							/>
						</Inline>
					</Inline>
				</Box>

				{/* Queue rows */}
				{queueNameWidths.map((w, i) => (
					<QueueRow key={i} nameWidth={w} />
				))}
			</Stack>
		</Box>
	);
};

export const QueueSettingsSkeleton = (): JSX.Element => (
	<Flex xcss={styles.wrapper}>
		<EmbedHeaderSkeleton />
		<Box xcss={styles.contentWrapper}>
			<QueueSettingsContentSkeleton />
		</Box>
	</Flex>
);
